
import React from 'react';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { ChevronUp, ChevronDown, Download, Folder, Folders } from 'lucide-react';
import DocumentItem from './DocumentItem';
import { Document, DocumentType } from '../../types/dataRoom';
import { formatDocumentType } from '../../utils/documentUtils';

interface DocumentSubCategory {
  type: DocumentType;
  label: string;
  documents: Document[];
}

interface DocumentTreeItemProps {
  category: string;
  subCategories: DocumentSubCategory[];
  isExpanded: boolean;
  expandedSubCategories: Record<string, boolean>;
  toggleCategory: (category: string) => void;
  toggleSubCategory: (subCategory: string) => void;
  toggleDocumentSelection: (documentId: string) => void;
  openEditTags: (documentId: string, tags: string[]) => void;
  openEditType: (documentId: string, type: DocumentType) => void;
  handleViewDocument: (document: string, name: string) => void;
  downloadDocuments: (documents: Document[]) => void;
  removeDocument: (documentId: string) => void;
}

const DocumentTreeItem: React.FC<DocumentTreeItemProps> = ({
  category,
  subCategories,
  isExpanded,
  expandedSubCategories,
  toggleCategory,
  toggleSubCategory,
  toggleDocumentSelection,
  openEditTags,
  openEditType,
  handleViewDocument,
  downloadDocuments,
  removeDocument
}) => {
  const allDocuments = subCategories.flatMap(sub => sub.documents);
  const totalDocuments = allDocuments.length;
  
  return (
    <div className="bg-white">
      {/* Parent Category */}
      <div 
        className="flex items-center p-1 cursor-pointer hover:bg-amber-50 border-b"
        onClick={() => toggleCategory(category)}
      >
        <div className="w-10">
          <Checkbox 
            checked={allDocuments.every(doc => doc.isSelected) && allDocuments.length > 0}
            onCheckedChange={(checked) => {
              allDocuments.forEach(doc => {
                if (checked && !doc.isSelected) toggleDocumentSelection(doc.id);
                if (!checked && doc.isSelected) toggleDocumentSelection(doc.id);
              });
            }}
          />
        </div>
        <div className="flex-1 flex items-center font-medium text-gray-800">
          {isExpanded ? 
            <ChevronDown className="h-4 w-4 mr-2 text-dataroom-primary" /> : 
            <ChevronUp className="h-4 w-4 mr-2 text-dataroom-primary" />
          }
          <Folders className="h-5 w-5 mr-2 text-dataroom-primary" />
          {category}
          <span className="ml-2 text-sm text-gray-500">({totalDocuments} documents)</span>
        </div>
        <div className="w-40 text-center">
          <Badge variant="outline" className="bg-dataroom-primary-light text-dataroom-primary border-dataroom-primary/30">
            {category}
          </Badge>
        </div>
        <div className="w-48 text-center text-sm text-gray-500">
          {totalDocuments > 0 ? new Date().toLocaleString('en-US', {
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
            day: 'numeric',
            month: 'short',
            year: 'numeric'
          }) : '-'}
        </div>
        <div className="w-36 flex justify-center">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              downloadDocuments(allDocuments);
            }}
            disabled={totalDocuments === 0}
          >
            <Download className="h-4 w-4" />
          </Button>
          <span className="w-12"></span>
        </div>
      </div>
      
      {/* Sub Categories */}
      {isExpanded && (
        <div className="bg-gray-50">
          {subCategories.map(subCategory => (
            <div key={subCategory.type} className="border-b border-gray-200 last:border-b-0">
              {/* Sub Category Header */}
              <div 
                className="flex items-center p-1 pl-16 cursor-pointer hover:bg-amber-50"
                onClick={() => toggleSubCategory(subCategory.type)}
              >
                <div className="w-10">
                  <Checkbox 
                    checked={subCategory.documents.every(doc => doc.isSelected) && subCategory.documents.length > 0}
                    onCheckedChange={(checked) => {
                      subCategory.documents.forEach(doc => {
                        if (checked && !doc.isSelected) toggleDocumentSelection(doc.id);
                        if (!checked && doc.isSelected) toggleDocumentSelection(doc.id);
                      });
                    }}
                  />
                </div>
                <div className="flex-1 flex items-center font-medium text-gray-700">
                  {expandedSubCategories[subCategory.type] ? 
                    <ChevronDown className="h-3 w-3 mr-2 text-dataroom-primary" /> : 
                    <ChevronUp className="h-3 w-3 mr-2 text-dataroom-primary" />
                  }
                  <Folder className="h-4 w-4 mr-2 text-dataroom-primary" />
                  {subCategory.label}
                  <span className="ml-2 text-sm text-gray-500">({subCategory.documents.length})</span>
                </div>
                <div className="w-fit text-center">
                  <Badge variant="outline" className="bg-white text-gray-600 border-gray-300 text-xs">
                    {formatDocumentType(subCategory.type)}
                  </Badge>
                </div>
                <div className="w-48 text-center text-sm text-gray-500">
                  {subCategory.documents.length > 0 ? subCategory.documents[0].uploadedAt.toLocaleString('en-US', {
                    hour: 'numeric',
                    minute: 'numeric',
                    hour12: true,
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric'
                  }) : '-'}
                </div>
                <div className="w-36 flex justify-center">
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadDocuments(subCategory.documents);
                    }}
                    disabled={subCategory.documents.length === 0}
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                  <span className="w-12"></span>
                </div>
              </div>
              
              {/* Individual Documents */}
              {expandedSubCategories[subCategory.type] && (
                <div className="pl-32 bg-white">
                  {subCategory.documents.map(doc => (
                    <DocumentItem
                      key={doc.id}
                      doc={doc}
                      toggleDocumentSelection={toggleDocumentSelection}
                      openEditTags={openEditTags}
                      openEditType={openEditType}
                      handleViewDocument={handleViewDocument}
                      downloadDocuments={downloadDocuments}
                      removeDocument={removeDocument}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DocumentTreeItem;
